import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Award, Download, Eye, Calendar, Star } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MyCertificatesPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, token } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [certificates, setCertificates] = useState([]);

  const fetchCertificates = useCallback(async () => {
    try {
      const response = await axios.get(
        `${API_URL}/api/exams/certificates/my`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCertificates(response.data.certificates);
    } catch (err) {
      console.error('Erro ao carregar certificados:', err);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/');
      return;
    }
    fetchCertificates();
  }, [isAuthenticated, navigate, fetchCertificates]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <div className="w-12 h-12 border-2 border-red-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-white">Carregando certificados...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black pt-24 pb-12">
      <div className="container mx-auto px-4 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <Button
            onClick={() => navigate('/')}
            variant="ghost"
            className="text-white hover:text-red-500 mb-4"
          >
            <ArrowLeft className="mr-2" size={20} />
            Voltar ao Início
          </Button>
          
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 bg-red-600/20 rounded-xl flex items-center justify-center">
              <Award className="text-red-500" size={28} />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-white">Meus Certificados</h1>
              <p className="text-gray-400">
                {certificates.length} {certificates.length === 1 ? 'certificado obtido' : 'certificados obtidos'}
              </p>
            </div>
          </div>
        </div>

        {/* Certificates List */}
        {certificates.length === 0 ? (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="py-16 text-center">
              <Award className="w-16 h-16 text-gray-600 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-white mb-2">
                Nenhum certificado ainda
              </h2>
              <p className="text-gray-400 mb-6">
                Complete os cursos com nota mínima de 8.0 para obter seus certificados.
              </p>
              <Button
                onClick={() => navigate('/')}
                className="bg-red-600 hover:bg-red-700"
              >
                Explorar Cursos
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4">
            {certificates.map((cert) => (
              <Card 
                key={cert.id} 
                className="bg-zinc-900 border-zinc-800 hover:border-red-800 transition-colors"
              >
                <CardContent className="p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-start gap-4">
                      <div className="w-12 h-12 bg-yellow-500/20 rounded-lg flex items-center justify-center flex-shrink-0">
                        <Award className="text-yellow-500" size={24} />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-white mb-1">
                          {cert.course_title}
                        </h3>
                        <div className="flex flex-wrap items-center gap-3 text-sm text-gray-400">
                          <span className="flex items-center gap-1">
                            <Calendar size={14} />
                            {formatDate(cert.issued_at)}
                          </span>
                          <span className="flex items-center gap-1">
                            <Star size={14} className="text-yellow-500" />
                            Nota: {cert.score.toFixed(1)}
                          </span>
                          <span>{cert.course_hours}h</span>
                        </div>
                        <p className="text-xs text-gray-500 mt-1 font-mono">
                          {cert.certificate_code}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex gap-2 sm:flex-shrink-0">
                      <Button
                        onClick={() => navigate(`/certificate/${cert.id}`)}
                        size="sm"
                        className="bg-red-600 hover:bg-red-700"
                      >
                        <Eye size={16} className="mr-1" />
                        Ver
                      </Button>
                      <Button
                        onClick={async () => {
                          const response = await axios.get(
                            `${API_URL}/api/exams/certificates/${cert.id}/download`,
                            { responseType: 'blob' }
                          );
                          const url = window.URL.createObjectURL(new Blob([response.data]));
                          const link = document.createElement('a');
                          link.href = url;
                          link.setAttribute('download', `certificado_${cert.certificate_code}.pdf`);
                          document.body.appendChild(link);
                          link.click();
                          link.remove();
                        }}
                        size="sm"
                        variant="outline"
                        className="border-zinc-700 text-white hover:bg-zinc-800"
                      >
                        <Download size={16} />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default MyCertificatesPage;
